import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { API_BASE, apiFetch } from "../lib/api";

interface CampChildRow {
  screeningId: string;
  childId: string;
  childName: string;
  childGrade: string | null;
  familyId?: string;
  findingCount: number;
  maxSeverity: "normal" | "mild" | "moderate" | "severe" | "critical";
  flaggedCount: number;
  phrReady: boolean;
  phrGeneratedAt: string | null;
}

interface CampDetail {
  camp: {
    id: string;
    schoolId: string;
    scheduledDate: string;
    status: string;
    expectedChildren: number | null;
    actualChildren: number | null;
    phrsGenerated: number | null;
  };
  live: {
    expected: number;
    ingested: number;
    phrsGenerated: number;
    phrErrors: number;
    whatsappSent: number;
    parentsLoggedIn: number;
    activationsBooked: number;
    subscriptions: number;
    recentErrors: Array<{ at: string; message: string }>;
    lastUpdated: string;
  } | null;
  children: CampChildRow[];
}

function CampDetail() {
  const { campId } = Route.useParams();
  const [data, setData] = useState<CampDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    apiFetch<CampDetail>(`/api/camps/${campId}`).then(setData).catch((e: Error) => setErr(e.message));
    const token = localStorage.getItem("skids.devToken") ?? "";
    const role = localStorage.getItem("skids.devRole") ?? "manager";
    const wsUrl = `${API_BASE.replace(
      "https://",
      "wss://",
    )}/api/camps/${campId}/ws?dev_token=${encodeURIComponent(token)}&dev_role=${role}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setErr("WebSocket error");
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(e.data);
        if (m.type === "state")
          setData((prev) => (prev ? { ...prev, live: m.state } : prev));
      } catch {}
    };
    return () => ws.close();
  }, [campId]);

  if (err) return <div className="panel text-red-600">{err}</div>;
  if (!data) return <div className="panel text-slate-500">Loading…</div>;

  const live = data.live;
  const expected = live?.expected ?? data.camp.expectedChildren ?? 0;
  const phrPct =
    expected > 0
      ? Math.round((((live?.phrsGenerated ?? 0) * 100) / expected))
      : 0;

  return (
    <div className="space-y-4">
      <div className="panel">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">
              Screen 8 detail
            </div>
            <h1 className="text-2xl font-semibold">Camp · {data.camp.scheduledDate}</h1>
          </div>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              connected ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {connected ? "live" : "offline"}
          </span>
        </div>
        <div className="text-xs text-slate-500">
          status:{" "}
          <span className="rounded bg-slate-100 px-1.5">{data.camp.status}</span>{" "}
          · expected {expected} · ingested{" "}
          <strong>{live?.ingested ?? data.camp.actualChildren ?? 0}</strong>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Tile label="PHRs generated" value={live?.phrsGenerated ?? 0} sub={`${phrPct}% of ${expected}`} />
        <Tile label="WhatsApp sent" value={live?.whatsappSent ?? 0} />
        <Tile label="Parents logged in" value={live?.parentsLoggedIn ?? 0} />
        <Tile label="Subscriptions" value={live?.subscriptions ?? 0} tone="ok" />
      </div>

      {expected > 0 && (
        <div className="panel">
          <div className="text-xs text-slate-500 mb-1">PHR generation progress</div>
          <div className="h-2 rounded bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-slate-900 transition-all"
              style={{ width: `${phrPct}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {live?.phrsGenerated ?? 0} / {expected}
          </div>
        </div>
      )}

      {live && live.recentErrors.length > 0 && (
        <div className="panel">
          <h3 className="text-sm font-medium mb-1 text-red-700">Recent errors</h3>
          <ul className="text-xs space-y-1">
            {live.recentErrors.map((e, i) => (
              <li key={i} className="text-red-600">
                <code className="text-slate-400">{new Date(e.at).toLocaleTimeString()}</code>{" "}
                {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="panel">
        <h3 className="text-sm font-medium mb-2">
          Children processed ({data.children.length})
        </h3>
        {data.children.length === 0 ? (
          <div className="text-xs text-slate-400">
            No screening rows yet — waiting for queue consumer to drain the manifest.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left">
              <tr>
                <th className="py-1">Child</th>
                <th>Grade</th>
                <th>Findings</th>
                <th>Worst severity</th>
                <th>Flagged</th>
                <th>PHR</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...data.children]
                .sort(
                  (a, b) =>
                    SEV_ORDER[b.maxSeverity] - SEV_ORDER[a.maxSeverity] ||
                    a.childName.localeCompare(b.childName),
                )
                .map((c) => (
                  <tr key={c.screeningId} className="border-t border-slate-100">
                    <td className="py-1">
                      {c.familyId ? (
                        <Link
                          to="/family/$familyId"
                          params={{ familyId: c.familyId }}
                          className="text-slate-700 hover:underline"
                        >
                          {c.childName}
                        </Link>
                      ) : (
                        c.childName
                      )}
                    </td>
                    <td>{c.childGrade ?? "—"}</td>
                    <td>{c.findingCount}</td>
                    <td>
                      <SeverityBadge sev={c.maxSeverity} />
                    </td>
                    <td>
                      {c.flaggedCount > 0 ? (
                        <span className="text-amber-700 font-medium">
                          {c.flaggedCount}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {c.phrReady ? (
                        <span className="text-green-700 text-xs">✓ ready</span>
                      ) : (
                        <span className="text-slate-400 text-xs">…</span>
                      )}
                    </td>
                    <td>
                      {c.phrReady && (
                        <a
                          href={`${API_BASE}/api/phr/${data.camp.id}/${c.childId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-slate-500 hover:underline"
                        >
                          PHR PDF →
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const SEV_ORDER = { normal: 0, mild: 1, moderate: 2, severe: 3, critical: 4 } as const;

function SeverityBadge({ sev }: { sev: keyof typeof SEV_ORDER }) {
  const cls =
    sev === "critical"
      ? "bg-red-200 text-red-900"
      : sev === "severe"
        ? "bg-red-100 text-red-800"
        : sev === "moderate"
          ? "bg-orange-100 text-orange-800"
          : sev === "mild"
            ? "bg-amber-100 text-amber-800"
            : "bg-green-100 text-green-800";
  return <span className={`text-xs rounded px-1.5 py-0.5 capitalize ${cls}`}>{sev}</span>;
}

function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: "ok";
}) {
  const cls = tone === "ok" ? "border-green-200 bg-green-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${cls}`}>
      <div className="text-3xl font-semibold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export const Route = createFileRoute("/camps/$campId")({ component: CampDetail });
